import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { API_URL } from '../../config';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import BillingHistorySection from '../../components/billing/customer/BillingHistorySection';
import PlatformOrgSpacesPanel from '../../components/admin/PlatformOrgSpacesPanel';

const SUBSCRIPTION_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'trialing', label: 'Trialing' },
  { value: 'past_due', label: 'Past due' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'cancelled', label: 'Cancelled' },
];

function toDateInputValue(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return '';
  }
}

function formatDisplayDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default function PlatformOrgSubscriptionEditor({ organizationId, onSaved, showBillingHistory = true }) {
  const [plansLoading, setPlansLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [slug, setSlug] = useState('');
  const [memberCount, setMemberCount] = useState(0);
  const [spaceCount, setSpaceCount] = useState(0);
  const [periodStartIso, setPeriodStartIso] = useState(null);
  const [form, setForm] = useState({
    plan_id: 'free',
    billing_cycle: 'monthly',
    status: 'active',
    current_period_end: '',
    ai_addon_enabled: false,
    faqs_addon_enabled: false,
    insights_analytics_enabled: false,
    billing_exempt: false,
  });
  const [billingHistory, setBillingHistory] = useState({ payments: [], activity: [] });

  useEffect(() => {
    let cancelled = false;
    setPlansLoading(true);
    axios
      .get(`${API_URL}/plans`)
      .then((res) => {
        if (!cancelled) setPlans(res.data || []);
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load plans');
      })
      .finally(() => {
        if (!cancelled) setPlansLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!organizationId) return undefined;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/platform/organizations/${organizationId}`),
      axios.get(`${API_URL}/platform/organizations/${organizationId}/billing-history`).catch(() => ({
        data: { payments: [], activity: [] },
      })),
    ])
      .then(([orgRes, histRes]) => {
        if (cancelled) return;
        const o = orgRes.data.organization;
        const s = orgRes.data.subscription;
        setOrgName(o.name);
        setSlug(o.slug);
        setMemberCount(o.member_count ?? 0);
        setSpaceCount(orgRes.data.space_count ?? 0);
        setPeriodStartIso(s.current_period_start || null);
        setForm({
          plan_id: s.plan_id,
          billing_cycle: s.billing_cycle || 'monthly',
          status: (s.status || 'active').toLowerCase(),
          current_period_end: toDateInputValue(s.current_period_end),
          ai_addon_enabled: !!s.ai_addon?.enabled,
          faqs_addon_enabled: !!s.faqs_addon?.enabled,
          insights_analytics_enabled: Boolean(
            (s.catalog_addons || []).find((a) => a.addon_id === 'insights_analytics')?.active ||
              s.insights_addon?.enabled
          ),
          billing_exempt: !!s.billing_exempt,
        });
        setBillingHistory({
          payments: histRes.data?.payments || [],
          activity: histRes.data?.activity || [],
        });
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load organization');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const save = async () => {
    if (!organizationId) return;
    if (!form.current_period_end) {
      toast.error('Set a subscription end date');
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/platform/organizations/${organizationId}/subscription`, {
        plan_id: form.plan_id,
        billing_cycle: form.billing_cycle,
        status: form.status,
        current_period_end: form.current_period_end,
        ai_addon_enabled: form.ai_addon_enabled,
        faqs_addon_enabled: form.faqs_addon_enabled,
        insights_analytics_enabled: form.insights_analytics_enabled,
        billing_exempt: form.billing_exempt,
      });
      toast.success('Subscription updated');
      onSaved?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
            : detail?.message || 'Update failed';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (!organizationId) {
    return (
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="font-[Manrope] text-base">Subscription</CardTitle>
          <CardDescription>Select an organization to view and edit billing.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (loading || plansLoading) {
    return (
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardContent className="flex items-center gap-2 py-10 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading…
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="font-[Manrope]">{orgName}</CardTitle>
          <CardDescription>
            {slug} · {memberCount} members · {spaceCount} spaces
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="plat-plan">Plan</Label>
            <select
              id="plat-plan"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              value={form.plan_id}
              onChange={(e) => setForm((f) => ({ ...f, plan_id: e.target.value }))}
            >
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="plat-cycle">Billing cycle</Label>
            <select
              id="plat-cycle"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              value={form.billing_cycle}
              onChange={(e) => setForm((f) => ({ ...f, billing_cycle: e.target.value }))}
            >
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="plat-status">Status</Label>
            <select
              id="plat-status"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            >
              {SUBSCRIPTION_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Period started</Label>
              <p className="h-9 flex items-center text-sm text-slate-600 dark:text-slate-300">
                {formatDisplayDate(periodStartIso)}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="plat-period-end">Subscription ends</Label>
              <input
                id="plat-period-end"
                type="date"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                value={form.current_period_end}
                onChange={(e) => setForm((f) => ({ ...f, current_period_end: e.target.value }))}
              />
              <p className="text-xs text-slate-500">
                Extend or shorten access (e.g. set to 31 Dec to keep the workspace active until then).
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
            <div>
              <Label htmlFor="plat-ai">AI documentation add-on</Label>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Lets this workspace use the editor assistant when keys are configured.
              </p>
            </div>
            <Switch
              id="plat-ai"
              checked={form.ai_addon_enabled}
              onCheckedChange={(v) => setForm((f) => ({ ...f, ai_addon_enabled: v }))}
            />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
            <div>
              <Label htmlFor="plat-faqs">Embeddable FAQs add-on</Label>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Enables FAQ sets and public iframe/script embeds for this workspace.
              </p>
            </div>
            <Switch
              id="plat-faqs"
              checked={form.faqs_addon_enabled}
              onCheckedChange={(v) => setForm((f) => ({ ...f, faqs_addon_enabled: v }))}
            />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
            <div>
              <Label htmlFor="plat-insights">Insights & Analytics</Label>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Unlocks the Insights dashboard for this workspace (views, searches, feedback).
              </p>
            </div>
            <Switch
              id="plat-insights"
              checked={form.insights_analytics_enabled}
              onCheckedChange={(v) => setForm((f) => ({ ...f, insights_analytics_enabled: v }))}
              data-testid="platform-org-insights-addon"
            />
          </div>

          <div
            className={`flex items-center justify-between gap-4 rounded-lg border p-3 ${
              form.billing_exempt
                ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20'
                : 'border-slate-200 dark:border-slate-700'
            }`}
          >
            <div>
              <Label htmlFor="plat-exempt">Complimentary workspace (never billed)</Label>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Every feature unlimited, no renewal charges, no dunning emails, and published docs are
                never suspended — whatever the plan or status above says. Use for internal, demo, and
                comped accounts. Turning this on also clears any existing billing suspension.
              </p>
            </div>
            <Switch
              id="plat-exempt"
              checked={form.billing_exempt}
              onCheckedChange={(v) => setForm((f) => ({ ...f, billing_exempt: v }))}
              data-testid="platform-org-billing-exempt"
            />
          </div>

          <Button onClick={save} disabled={saving} data-testid="platform-org-subscription-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save subscription
          </Button>
        </CardContent>
      </Card>

      {showBillingHistory ? (
        <BillingHistorySection
          payments={billingHistory.payments}
          activity={billingHistory.activity}
          compact
        />
      ) : null}

      <PlatformOrgSpacesPanel organizationId={organizationId} onChanged={onSaved} />
    </div>
  );
}
